import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Award, Clock, FileText, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const features = [
  { icon: Shield, title: 'Wide Range of Personal & Business Insurance Options' },
  { icon: Users, title: 'Experienced, Licensed Insurance Professionals' },
  { icon: Award, title: 'Access to Multiple Top-Rated Insurance Carriers' },
  { icon: Clock, title: 'Fast Certificates of Insurance (COI)' },
  { icon: FileText, title: 'Personalized Risk Assessments' },
  { icon: CheckCircle, title: 'Clear Guidance Every Step of the Way' }];


  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center min-h-[600px] flex items-center"
        style={{
          backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.5), rgba(0,0,0,0.3)), url(https://images.unsplash.com/photo-1714974528703-e5ad41abc259)',
          backgroundPosition: 'center'
        }}>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side */}
            <div className="text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Independent, Experienced, Focused on You
              </h1>
              <p className="text-lg md:text-xl mb-8 text-slate-100 leading-relaxed">
                We work with multiple top-rated carriers to find coverage that truly fits your needs and budget.
              </p>
              <p className="text-xl font-semibold mb-8 text-[#C9A361]">
                Secure today, plan confidently for tomorrow
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-[#C9A361] hover:bg-[#B8925A] text-white px-8 py-6 text-lg transition-all hover:scale-105">
                    Start Planning
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Side - Info Card */}
            <div className="hidden lg:block">
              <Card className="bg-white/95 backdrop-blur-sm shadow-2xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-slate-900">Plan for life's big moments</h3>
                  <div className="space-y-4">
                    <Link to="/products" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-[#F9F7F3] transition-all group">
                      <span className="font-semibold text-slate-900">Commercial Insurance</span>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#C9A361] group-hover:translate-x-1 transition-all" />
                    </Link>
                    <Link to="/products" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-[#F9F7F3] transition-all group">
                      <span className="font-semibold text-slate-900">Personal Insurance</span>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#C9A361] group-hover:translate-x-1 transition-all" />
                    </Link>
                    <Link to="/products" className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-[#F9F7F3] transition-all group">
                      <span className="font-semibold text-slate-900">All Products</span>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#C9A361] group-hover:translate-x-1 transition-all" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Clients Choose Progress Insurance Solutions</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We combine industry expertise with personalized service to deliver insurance solutions that truly protect what matters most.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-2 hover:border-[#C9A361] transition-all hover:shadow-lg group">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#F5F1E8] p-3 rounded-lg group-hover:bg-[#C9A361] transition-colors">
                        <Icon className="w-6 h-6 text-[#C9A361] group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{feature.title}</h3>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comprehensive Protection Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Comprehensive Protection, Complete Peace of Mind</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              From home and auto to small business coverage, we provide all-in-one solutions that safeguard what matters most — so you can focus on living and growing with confidence.
            </p>
            <div className="mt-8">
              <Link to="/contact">
                <Button size="lg" className="bg-[#C9A361] hover:bg-[#B8925A] text-white px-8 py-6 text-lg transition-all hover:scale-105">
                  Request Your Free Quote Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Commercial Insurance */}
            <div className="group">
              <div className="relative overflow-hidden rounded-2xl mb-6 h-80">
                <img 
                  src="https://images.unsplash.com/photo-1762341107834-a3437dd0ae62" 
                  alt="Commercial Insurance"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
              </div>
              <h3 className="text-3xl font-bold mb-4 text-slate-900">Protection That Works as Hard as You Do</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                At Progress Insurance Solutions, we understand that running a business comes with responsibility and risk. Our commercial insurance solutions are designed to protect your operations, your employees, and your long-term success.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Whether you run a small startup or manage an established company, we provide tailored solutions built around your industry and growth plans.
              </p>
              <p className="text-slate-700 font-semibold mb-6">
                Comprehensive protection for your business at every stage
              </p>
              <Link to="/products">
                <Button className="bg-[#C9A361] hover:bg-[#B8925A] text-white transition-all hover:scale-105">
                  Explore Commercial Coverage Options
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Personal Insurance */}
            <div className="group">
              <div className="relative overflow-hidden rounded-2xl mb-6 h-80">
                <img 
                  src="https://images.unsplash.com/photo-1758687126877-b37052a20a4d" 
                  alt="Personal Insurance"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
              </div>
              <h3 className="text-3xl font-bold mb-4 text-slate-900">Safeguarding Your Home, Family, and Future</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Your home, your vehicle, and your family deserve reliable protection. Insurance isn't just about policies—it's about your peace of mind.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Progress Insurance Solutions helps you select personal coverage that fits your lifestyle and budget, whether you're safeguarding your home, staying protected on the road, or preparing for life's uncertainties.
              </p>
              <p className="text-slate-700 font-semibold mb-6">
                Clear options, honest guidance, dependable protection
              </p>
              <Link to="/products">
                <Button className="bg-[#C9A361] hover:bg-[#B8925A] text-white transition-all hover:scale-105">
                  Explore Personal Coverage Options
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Protect What Matters Most?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Our team at Progress Insurance Solutions is here to help you find the right coverage with clarity and confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#C9A361] hover:bg-[#B8925A] text-white px-8 py-6 text-lg transition-all hover:scale-105">
                Request a Free Quote
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg transition-all">
                Speak With an Advisor
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
